import { useState } from "react";
import { Section, Eyebrow, H2, Lead, GlassCard } from "../components/site/Primitives";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import api from "../lib/api";
import { SUBSIDIARIES } from "../data/subsidiaries";

const ROLES = ["Construction Manager", "Site Superintendent", "Project Engineer", "Apprentice Electrician", "Apprentice Plumber",
  "Property Manager", "Leasing Agent", "Maintenance Tech", "Case Manager", "Behavioral Health Counselor",
  "Event Center — Banquet Captain", "Event Center — Event Coordinator", "CDL Driver", "Trade Instructor", "Marketing Lead"];

export default function Careers() {
  return (
    <div>
      <Section className="pt-40">
        <Eyebrow>Careers at 220 Inc.</Eyebrow>
        <H2 className="max-w-4xl">Build a career that builds a community.</H2>
        <Lead className="mt-6">
          Veterans, trades workers, hospitality talent, technologists, and operators — every 220
          subsidiary is hiring. Veteran applicants receive priority review.
        </Lead>
      </Section>

      <Section className="pt-0">
        <Eyebrow>Open Roles</Eyebrow>
        <div className="mt-8 border border-white/10">
          {ROLES.map((r, i) => (
            <div key={r} className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 ${i ? "border-t border-white/10" : ""} hover:bg-white/[0.02]`}>
              <div>
                <div className="font-display text-lg">{r}</div>
                <div className="text-xs text-white/40 uppercase tracking-[0.2em] mt-1">220 Inc. · Columbus, GA</div>
              </div>
              <a href="#apply" className="btn-ghost text-sm">Apply <ArrowRight size={14} /></a>
            </div>
          ))}
        </div>
      </Section>

      <Section id="apply" className="border-t border-white/5">
        <ApplyForm />
      </Section>
    </div>
  );
}

function ApplyForm() {
  const [f, setF] = useState({
    full_name: "", email: "", phone: "", position: "", subsidiary: "construction",
    veteran: false, experience_years: 0, resume_link: "", cover_letter: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/careers/apply", { ...f, experience_years: Number(f.experience_years) || 0 });
      toast.success("Application received. Veteran applicants are reviewed within 5 business days.");
      setF({ ...f, full_name: "", email: "", phone: "", position: "", resume_link: "", cover_letter: "" });
    } catch { toast.error("Could not submit application."); }
  };
  return (
    <GlassCard className="p-8 md:p-12" data-testid="career-form">
      <Eyebrow>Apply</Eyebrow>
      <H2>Submit your application.</H2>
      <form onSubmit={submit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        <I label="Full Name *" required value={f.full_name} onChange={(e) => set("full_name", e.target.value)} />
        <I label="Email *" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)} />
        <I label="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} />
        <I label="Position *" required value={f.position} onChange={(e) => set("position", e.target.value)} />
        <S label="Subsidiary" value={f.subsidiary} onChange={(e) => set("subsidiary", e.target.value)}
          options={SUBSIDIARIES.map((s) => ({ v: s.slug, l: s.name }))} />
        <I label="Years of Experience" type="number" min="0" value={f.experience_years} onChange={(e) => set("experience_years", e.target.value)} />
        <I label="Resume Link" placeholder="https://" value={f.resume_link} onChange={(e) => set("resume_link", e.target.value)} />
        <label className="flex items-center gap-3 mt-3">
          <input type="checkbox" checked={f.veteran} onChange={(e) => set("veteran", e.target.checked)} className="accent-[#D4AF37] w-4 h-4" />
          <span className="text-sm text-white/80">I am a U.S. military veteran or active-duty service member</span>
        </label>
        <label className="block md:col-span-2">
          <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Cover Letter</span>
          <textarea rows={5} className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" value={f.cover_letter} onChange={(e) => set("cover_letter", e.target.value)} />
        </label>
        <div className="md:col-span-2">
          <button type="submit" className="btn-primary" data-testid="career-submit">Submit Application <ArrowRight size={14} /></button>
        </div>
      </form>
    </GlassCard>
  );
}

function I({ label, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <input className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" {...props} />
    </label>
  );
}
function S({ label, options, ...props }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      <select className="w-full bg-[#0e0e10] border border-white/10 px-3 py-3 text-white text-sm focus:outline-none focus:border-gold" {...props}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
